/**
 * 检查项记录管理 - 列表页
 * UI 对齐 web后台.html 标准（明阳设计稿）：
 *  - 6 KPI 卡片（纯展示）
 *  - 筛选栏：基地/项目/编号/机型/工序 多选 + 检验序号模糊 + 日期范围(span2) + 状态多选(span3) + 重置
 *  - 表头 13 列
 *  - 行操作：详情 + 按 status 动态展示（提交整改/发起复议/审核通过/驳回整改/复议通过/驳回复议）
 *  - 顶部仅一个"导出"按钮
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  DatePicker,
  Input,
  Select,
  Spin,
  Table,
  Tooltip,
  message
} from 'antd'
import {
  AppstoreOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  CheckFilterCascade,
  CheckListItem,
  CheckListParams,
  CheckRecordStatus,
  CheckStatsResponse,
  MediaResourceParam,
  fetchCheckFilterOptions,
  fetchCheckList,
  fetchCheckStats,
  startCheckExport,
  updateCheckStatus
} from '../api/check'
import { selectGroupAndUser } from '../api/client'
import {
  CHECK_ACTIONS_BY_STATUS,
  CHECK_STATS_CARDS,
  CHECK_STATUS_BG,
  CHECK_STATUS_COLOR,
  CHECK_STATUS_FILTER_OPTIONS,
  CHECK_STATUS_LABEL,
  CheckActionConfig
} from '../constants/check'
import ExportModal from '../shared/ExportModal'
import StatusTag from '../shared/StatusTag'
import StatusActionModal from './StatusActionModal'
import FixModal from './FixModal'
import ReviewModal from './ReviewModal'

const { Option } = Select
const { RangePicker } = DatePicker

// KPI 卡片图标查表（card.icon 来自 constants/check.ts 的 CHECK_STATS_CARDS）
const STAT_ICONS: Record<string, React.ComponentType<any>> = {
  appstore: AppstoreOutlined,
  'check-circle': CheckCircleOutlined,
  'close-circle': CloseCircleOutlined,
  'clock-circle': ClockCircleOutlined,
  'exclamation-circle': ExclamationCircleOutlined,
  audit: AuditOutlined
}
const StatIcon = ({ name }: { name: string }) => {
  const C = STAT_ICONS[name]
  return C ? <C /> : null
}

// 多选 Select 折叠 placeholder：统一显示为"等 N 项"
const compactPlaceholder = (omitted: any[]) => `等 ${omitted.length} 项`

interface ListProps {
  dispatch: (...args: any[]) => any
  state: any
  onOpenDetail: (checkId: number) => void
  /** 跳转到下载管理页 */
  onGoToDownloads?: () => void
}

/**
 * 筛选状态：列表接口 status 支持单值或数组；UI 上为多选，内部用 number[]。
 */
interface FilterState {
  base_name: string[]
  project_name: string[]
  project_number: string[]
  machine_model: string[]
  process_name: string[]
  inspection: string
  start_time: string
  end_time: string
  status: CheckRecordStatus[]
  page: number
  page_size: number
}

const initialFilters: FilterState = {
  base_name: [],
  project_name: [],
  project_number: [],
  machine_model: [],
  process_name: [],
  inspection: '',
  start_time: '',
  end_time: '',
  status: [],
  page: 1,
  page_size: 20
}

const initialStats: CheckStatsResponse = {
  total: 0,
  passed: 0,
  failed: 0,
  pending: 0,
  manual_reviewing: 0,
  appealing: 0
}

// KPI 配置来源于 constants/check.ts 的 CHECK_STATS_CARDS

const List: React.FC<ListProps> = ({
  dispatch,
  state,
  onOpenDetail,
  onGoToDownloads
}) => {
  // 权限：仅普通成员只能看到 提交整改 / 发起复议；
  // 管理员可见全部操作（审核通过、驳回整改、复议通过、驳回复议等）
  //
  // 角色完全由后端按请求信封顶层 user_id 自动判定，前端不再做本地推断：
  //  - 响应里 role >= 1 → 管理员（预留 2/3 自动兼容）
  //  - 响应里 role === 0 或 接口未返回 role → 按普通成员处理（最保守，避免误授权）
  //  详见 Web管理后台-检查项记录管理-权限收口接口变更说明.md
  const [backendRole, setBackendRole] = useState<number | undefined>(undefined)
  const isAdmin = (backendRole ?? 0) >= 1
  const [filters, setFilters] = useState<FilterState>(initialFilters)
  const [stats, setStats] = useState<CheckStatsResponse>(initialStats)
  const [list, setList] = useState<CheckListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [statsLoading, setStatsLoading] = useState(false)

  // 模态框分三类：通用确认/驳回（StatusActionModal），整改 FixModal，复议 ReviewModal
  const [actionModal, setActionModal] = useState<{
    visible: boolean
    action: CheckActionConfig | null
    record: CheckListItem | null
  }>({ visible: false, action: null, record: null })
  const [fixModal, setFixModal] = useState<CheckListItem | null>(null)
  const [reviewModal, setReviewModal] = useState<CheckListItem | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)

  const loadStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const s = await fetchCheckStats(dispatch)
      setStats(s)
    } catch (e) {
      console.warn('[check] 统计加载失败:', e)
    } finally {
      setStatsLoading(false)
    }
  }, [dispatch])

  // user_id / role 给 list 接口按身份过滤数据用.从整 state 里 select 出 primitive
  // (string)再进 useMemo deps —— 直接把 state 进 deps 会因为 state 整 store 引用
  // 频繁变化导致 apiParams 反复重算 / loadList useEffect 反复触发列表 fetch.
  const { user_id: meUserId, role: meRole } = selectGroupAndUser(state)

  const apiParams: CheckListParams = useMemo(
    () => ({
      // 文档支持精确匹配；多个值时取第一个传后端，后端可后续扩展为 IN 查询
      // 这里先简化：多值时仅传第 1 个
      base_name: filters.base_name[0] || '',
      project_name: filters.project_name[0] || '',
      project_number: filters.project_number[0] || '',
      machine_model: filters.machine_model[0] || '',
      process_name: filters.process_name[0] || '',
      inspection: filters.inspection || '',
      start_time: filters.start_time || '',
      end_time: filters.end_time || '',
      status: filters.status.length === 0 ? null : filters.status,
      page: filters.page,
      page_size: filters.page_size,
      user_id: meUserId,
      role: meRole
    }),
    [filters, meUserId, meRole]
  )

  const loadList = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchCheckList(dispatch, apiParams)
      setList(data.list || [])
      setTotal(data.total || 0)
      // 后端按 user_id 自动判定的角色，覆盖前端本地推断（向前兼容期 role 可能缺失）
      if (typeof data.role === 'number') setBackendRole(data.role)
    } catch (e) {
      message.error((e as Error)?.message || '加载列表失败')
    } finally {
      setLoading(false)
    }
  }, [dispatch, apiParams])

  useEffect(() => {
    loadStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadList()
  }, [loadList])

  const refreshAll = useCallback(() => {
    loadList()
    loadStats()
  }, [loadList, loadStats])

  const handleFilterChange = useCallback((patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }))
  }, [])

  const handleExportClick = useCallback(async () => {
    const { group_id, user_id } = selectGroupAndUser(state)
    if (!group_id || !user_id) {
      message.error('未获取到团队或用户信息')
      return
    }
    try {
      await startCheckExport(dispatch, {
        base_name: filters.base_name[0] || '',
        project_name: filters.project_name[0] || '',
        project_number: filters.project_number[0] || '',
        machine_model: filters.machine_model[0] || '',
        process_name: filters.process_name[0] || '',
        inspection: filters.inspection || '',
        start_time: filters.start_time || '',
        end_time: filters.end_time || '',
        status: filters.status.length === 0 ? null : filters.status,
        group_id,
        user_id
      })
      setExportOpen(true)
    } catch (e) {
      message.error((e as Error)?.message || '导出失败')
    }
  }, [dispatch, state, filters])

  const handleReset = useCallback(() => {
    setFilters(initialFilters)
  }, [])

  /**
   * 根据 action 决定路由到哪种模态框：
   *  - submit_rectify → FixModal（含媒体管理 + 整改说明）
   *  - submit_appeal  → ReviewModal（含原因选择）
   *  - 其他           → StatusActionModal（通用确认/驳回）
   */
  const openAction = useCallback(
    (record: CheckListItem, action: CheckActionConfig) => {
      if (action.action === 'submit_rectify') {
        setFixModal(record)
      } else if (action.action === 'submit_appeal') {
        setReviewModal(record)
      } else {
        setActionModal({ visible: true, action, record })
      }
    },
    []
  )

  const handleConfirmAction = useCallback(
    async (remark: string) => {
      const { action, record } = actionModal
      if (!action || !record) return
      const { group_id, user_id } = selectGroupAndUser(state)
      if (!group_id || !user_id) {
        message.error('未获取到团队或用户信息')
        return
      }
      setSubmitting(true)
      try {
        await updateCheckStatus(dispatch, {
          check_id: record.check_id,
          action: action.action,
          remark,
          group_id,
          user_id
        })
        message.success('操作成功')
        setActionModal({ visible: false, action: null, record: null })
        refreshAll()
      } catch (e) {
        message.error((e as Error)?.message || '操作失败')
      } finally {
        setSubmitting(false)
      }
    },
    [actionModal, dispatch, state, refreshAll]
  )

  // FixModal / ReviewModal 提交回调
  const handleFixSubmit = useCallback(
    async (payload: {
      remark: string
      media_resources: MediaResourceParam[]
    }) => {
      if (!fixModal) return
      const { group_id, user_id } = selectGroupAndUser(state)
      if (!group_id || !user_id) {
        message.error('未获取到团队或用户信息')
        return
      }
      setSubmitting(true)
      try {
        await updateCheckStatus(dispatch, {
          check_id: fixModal.check_id,
          action: 'submit_rectify',
          remark: payload.remark,
          media_resources: payload.media_resources,
          group_id,
          user_id
        })
        message.success('已提交整改')
        setFixModal(null)
        refreshAll()
      } catch (e) {
        message.error((e as Error)?.message || '提交失败')
      } finally {
        setSubmitting(false)
      }
    },
    [fixModal, dispatch, state, refreshAll]
  )

  const handleReviewSubmit = useCallback(
    async (payload: { reason: string; remark: string }) => {
      if (!reviewModal) return
      const { group_id, user_id } = selectGroupAndUser(state)
      if (!group_id || !user_id) {
        message.error('未获取到团队或用户信息')
        return
      }
      // 把 reason + 自由 remark 合并为 remark 字段提交（接口仅支持单个备注）
      const combined = [`复议原因：${payload.reason}`, payload.remark]
        .filter(Boolean)
        .join('\n')
      setSubmitting(true)
      try {
        await updateCheckStatus(dispatch, {
          check_id: reviewModal.check_id,
          action: 'submit_appeal',
          remark: combined,
          group_id,
          user_id
        })
        message.success('已发起复议')
        setReviewModal(null)
        refreshAll()
      } catch (e) {
        message.error((e as Error)?.message || '提交失败')
      } finally {
        setSubmitting(false)
      }
    },
    [reviewModal, dispatch, state, refreshAll]
  )

  const columns = useMemo(
    () => [
      {
        title: '检查项',
        dataIndex: 'inspection',
        key: 'inspection',
        width: 200,
        ellipsis: true,
        render: (text: string, row: CheckListItem) => (
          <Tooltip title={text}>
            <a
              onClick={() => onOpenDetail(row.check_id)}
              style={{
                color: '#1F2937',
                fontWeight: 500
              }}
            >
              {text || '-'}
            </a>
          </Tooltip>
        )
      },
      {
        title: '基地',
        dataIndex: 'base_name',
        key: 'base_name',
        width: 110,
        ellipsis: true
      },
      {
        title: '项目',
        dataIndex: 'project_name',
        key: 'project_name',
        width: 180,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text}>
            <span>{text || '-'}</span>
          </Tooltip>
        )
      },
      {
        title: '整机编号',
        dataIndex: 'project_number',
        key: 'project_number',
        width: 100,
        render: (code: string) =>
          code ? (
            <span className="myse-soft-tag myse-soft-tag--blue">{code}</span>
          ) : (
            <span style={{ color: '#9CA3AF' }}>-</span>
          )
      },
      {
        title: '机型',
        dataIndex: 'machine_model',
        key: 'machine_model',
        width: 140,
        ellipsis: true,
        render: (text: string) => (
          <span style={{ fontFamily: 'Inter, system-ui' }}>{text || '-'}</span>
        )
      },
      {
        title: '装配工序',
        dataIndex: 'process_name',
        key: 'process_name',
        width: 100
      },
      {
        title: '检验序号',
        dataIndex: 'inspection_seq',
        key: 'inspection_seq',
        width: 110,
        // 文档 list 字段未独立返回检验序号编号，沿用 inspection 拆首段
        render: (_: any, row: CheckListItem) => {
          const segs = (row.inspection || '').split('-')
          const seq = segs.length > 1 ? segs[0] : '-'
          return (
            <span
              style={{
                color: '#6B7280',
                fontFamily: 'Inter, system-ui'
              }}
            >
              {seq}
            </span>
          )
        }
      },
      {
        title: '关联用户',
        dataIndex: 'user_names',
        key: 'user_names',
        width: 160,
        render: (names: string, row: CheckListItem) => {
          const list = (names || row.user_ids || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
          if (list.length === 0)
            return <span style={{ color: '#9CA3AF' }}>-</span>
          return (
            <Tooltip title={list.join('、')}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {list.map((u) => (
                  <span
                    key={u}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      color: '#374151'
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 999,
                        background: 'linear-gradient(135deg,#A4C8FF,#3B7DEB)',
                        color: '#fff',
                        fontSize: 10,
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 600
                      }}
                    >
                      {u.charAt(0)}
                    </span>
                    {u}
                  </span>
                ))}
              </div>
            </Tooltip>
          )
        }
      },
      {
        title: '最新媒体时间',
        dataIndex: 'update_time',
        key: 'update_time',
        width: 160,
        render: (t: string) => (
          <span
            style={{
              color: '#6B7280',
              fontFamily: 'Inter, system-ui',
              whiteSpace: 'nowrap'
            }}
          >
            {t || '-'}
          </span>
        )
      },
      {
        title: '检查项状态',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        fixed: 'right' as const,
        render: (s: CheckRecordStatus, row: CheckListItem) => (
          <StatusTag
            label={row.status_label || CHECK_STATUS_LABEL[s] || String(s)}
            color={CHECK_STATUS_COLOR[s]}
            bg={CHECK_STATUS_BG[s]}
          />
        )
      },
      {
        title: '操作',
        key: 'action',
        width: 220,
        fixed: 'right' as const,
        render: (_: any, row: CheckListItem) => {
          const allActions = CHECK_ACTIONS_BY_STATUS[row.status] || []
          // 整改/复议类(submit_rectify、submit_appeal) 仅普通成员；其余审核/驳回类
          // (review_pass、review_reject、appeal_pass、appeal_reject、override_reject)
          // 仅管理员。两组互斥，按 role 分流（详见接口文档表四「前端按钮渲染规则」）。
          const isUserAction = (a: string) =>
            a === 'submit_rectify' || a === 'submit_appeal'
          const actions = allActions.filter((a) =>
            isAdmin ? !isUserAction(a.action) : isUserAction(a.action)
          )
          return (
            <span className="myse-row-actions">
              <a onClick={() => onOpenDetail(row.check_id)}>详情</a>
              {actions.map((cfg) => {
                // action 颜色映射：提交整改=橙 / 发起复议=绿 / 驳回=红 / 通过=绿
                let cls = 'myse-link myse-link--nav'
                if (cfg.danger) cls = 'myse-link myse-link--danger'
                else if (cfg.action === 'submit_rectify')
                  cls = 'myse-link myse-link--primary'
                else if (
                  cfg.action === 'submit_appeal' ||
                  cfg.action === 'review_pass' ||
                  cfg.action === 'appeal_pass'
                )
                  cls = 'myse-link myse-link--success'
                return (
                  <a
                    key={cfg.action}
                    className={cls}
                    onClick={() => openAction(row, cfg)}
                  >
                    {cfg.label}
                  </a>
                )
              })}
            </span>
          )
        }
      }
    ],
    [onOpenDetail, openAction, isAdmin]
  )

  const { user_id, group_id } = selectGroupAndUser(state)

  // 筛选下拉选项 — 走 filter_options 接口拿权威候选,任意维度都是平等的:
  //   - 5 个 Select 维度(基地/项目/编号/机型/工序)互相联动
  //   - mount 时全部维度都拉一次全量(filters 不传)
  //   - 任一维度的 value 变化 → 重新拉"其他 4 个维度"的候选,把当前已选作为
  //     cascade filters 收窄。查 X 维度时 filters 里要排除 X 自身,否则后端
  //     只回显已选项,看不到其他可选
  //   - 不强制"先选基地",任意维度都能先选,默认全空 = 全量数据
  const [baseOptions, setBaseOptions] = useState<string[]>([])
  const [projectOptions, setProjectOptions] = useState<string[]>([])
  const [codeOptions, setCodeOptions] = useState<string[]>([])
  const [modelOptions, setModelOptions] = useState<string[]>([])
  const [procOptions, setProcOptions] = useState<string[]>([])

  // filter_options.options 是 string[] | {value,label}[] 联合类型(status 字段
  // 走对象,其他走 string),这里业务只用 string 维度,小工具把 string 项过滤出来。
  const pickStringOptions = (
    options:
      | string[]
      | Array<{ value: number; label: string }>
      | undefined
      | null
  ): string[] => {
    if (!Array.isArray(options)) return []
    const out: string[] = []
    for (const v of options) {
      if (typeof v === 'string') out.push(v)
    }
    return out
  }

  // 5 个维度的下拉选项 — 任一维度变化都按当前其他维度的已选值做 cascade
  useEffect(() => {
    // 当前所有维度的已选 — 用来给"其他维度"的查询做 cascade
    const allFilters: CheckFilterCascade = {
      base_name: filters.base_name,
      project_name: filters.project_name,
      project_number: filters.project_number,
      machine_model: filters.machine_model,
      process_name: filters.process_name
    }

    type Field =
      | 'base_name'
      | 'project_name'
      | 'project_number'
      | 'machine_model'
      | 'process_name'

    const fetchField = (field: Field, setter: (v: string[]) => void) => {
      // 查 X 维度的候选时,filters 里要排除 X 自身,否则后端只回显当前已选项
      const without = { ...allFilters }
      delete (without as any)[field]
      fetchCheckFilterOptions(dispatch, {
        field,
        limit: 200,
        filters: without
      })
        .then((r) => setter(pickStringOptions(r.options)))
        .catch(() => setter([]))
    }
    fetchField('base_name', setBaseOptions)
    fetchField('project_name', setProjectOptions)
    fetchField('project_number', setCodeOptions)
    fetchField('machine_model', setModelOptions)
    fetchField('process_name', setProcOptions)
  }, [
    dispatch,
    filters.base_name,
    filters.project_name,
    filters.project_number,
    filters.machine_model,
    filters.process_name
  ])

  return (
    <div className="check-list">
      {/* 页面标题 + 副标题 + 导出按钮（对齐 web后台.html + 其他模块） */}
      <div className="myse-page-header">
        <div>
          <h1 className="myse-page-title">检查项管理</h1>
          <div className="myse-page-subtitle">
            实时查看检查项采集记录与 AI
            审核结果，按状态处理整改、复议与人工审核流转。
          </div>
        </div>
        <Button
          icon={<DownloadOutlined />}
          disabled={!group_id || !user_id || total === 0}
          onClick={handleExportClick}
        >
          导出
        </Button>
      </div>

      {/* 顶部 6 KPI 卡片（纯展示，对齐 web后台.html） */}
      <Spin spinning={statsLoading} delay={120}>
        <div className="mingyang-stats mingyang-stats--cols-6">
          {CHECK_STATS_CARDS.map((card) => (
            <div key={card.key} className="mingyang-stats__card">
              <div
                className="mingyang-stats__icon"
                style={{ background: card.bg, color: card.color }}
              >
                <StatIcon name={card.icon} />
              </div>
              <div className="mingyang-stats__content">
                <div
                  className="mingyang-stats__value"
                  style={{ color: '#1F2937' }}
                >
                  {stats[card.key] ?? 0}
                </div>
                <div className="mingyang-stats__label">{card.label}</div>
              </div>
            </div>
          ))}
        </div>
      </Spin>

      {/* 筛选栏：两层结构 — 第一行 6 列 grid 整齐，第二行 flex 布局 */}
      <div className="mingyang-filterbar mingyang-filterbar--stacked">
        {/* 第一行：6 个 1fr 等分 */}
        <div className="mingyang-filterbar__grid mingyang-filterbar__grid--cols-6">
          <Select
            mode="multiple"
            className="myse-flat-select"
            maxTagCount={1}
            maxTagPlaceholder={compactPlaceholder}
            value={filters.base_name}
            onChange={(v: string[]) => handleFilterChange({ base_name: v })}
            placeholder="基地名称"
            allowClear
          >
            {baseOptions.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
          <Select
            mode="multiple"
            className="myse-flat-select"
            maxTagCount={1}
            maxTagPlaceholder={compactPlaceholder}
            value={filters.project_name}
            onChange={(v: string[]) => handleFilterChange({ project_name: v })}
            placeholder="项目名称"
            allowClear
          >
            {projectOptions.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
          <Select
            mode="multiple"
            className="myse-flat-select"
            maxTagCount={1}
            maxTagPlaceholder={compactPlaceholder}
            value={filters.project_number}
            onChange={(v: string[]) =>
              handleFilterChange({ project_number: v })
            }
            placeholder="整机编号"
            allowClear
          >
            {codeOptions.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
          <Select
            mode="multiple"
            className="myse-flat-select"
            maxTagCount={1}
            maxTagPlaceholder={compactPlaceholder}
            value={filters.machine_model}
            onChange={(v: string[]) => handleFilterChange({ machine_model: v })}
            placeholder="整机机型"
            allowClear
          >
            {modelOptions.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
          <Select
            mode="multiple"
            className="myse-flat-select"
            maxTagCount={1}
            maxTagPlaceholder={compactPlaceholder}
            value={filters.process_name}
            onChange={(v: string[]) => handleFilterChange({ process_name: v })}
            placeholder="装配工序"
            allowClear
          >
            {procOptions.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
          <Input
            value={filters.inspection}
            onChange={(e) => handleFilterChange({ inspection: e.target.value })}
            placeholder="检验序号"
            allowClear
          />
        </div>

        {/* 第二行：flex — 日期固定宽 + 状态 flex 自适应 + 重置紧贴右 */}
        <div className="mingyang-filterbar__flex">
          <RangePicker
            className="mingyang-filterbar__date"
            value={
              filters.start_time && filters.end_time
                ? [dayjs(filters.start_time), dayjs(filters.end_time)]
                : (null as any)
            }
            onChange={(dates) => {
              if (!dates || !dates[0]) {
                handleFilterChange({ start_time: '', end_time: '' })
              } else {
                handleFilterChange({
                  start_time: dates[0] ? dates[0].format('YYYY-MM-DD') : '',
                  end_time: dates[1] ? dates[1].format('YYYY-MM-DD') : ''
                })
              }
            }}
            format="YYYY-MM-DD"
          />

          <Select
            mode="multiple"
            maxTagCount={2}
            maxTagPlaceholder={compactPlaceholder}
            className="mingyang-filterbar__flex-grow myse-flat-select"
            value={filters.status}
            onChange={(v: CheckRecordStatus[]) =>
              handleFilterChange({ status: v })
            }
            placeholder="检查项状态"
            allowClear
          >
            {CHECK_STATUS_FILTER_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>

          <a className="myse-reset-btn" onClick={handleReset}>
            <ReloadOutlined />
            重置
          </a>
        </div>
      </div>

      {/* 表格 */}
      <div className="mingyang-table-wrapper">
        <Table
          rowKey="check_id"
          loading={loading}
          columns={columns}
          dataSource={list}
          scroll={{ x: 1700 }}
          pagination={{
            current: filters.page,
            pageSize: filters.page_size,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (n) => `共 ${n} 条`,
            onChange: (p, s) =>
              setFilters((prev) => ({ ...prev, page: p, page_size: s || 20 })),
            onShowSizeChange: (_, s) =>
              setFilters((prev) => ({ ...prev, page: 1, page_size: s }))
          }}
          size="middle"
        />
      </div>

      {/* 通用确认/驳回（审核通过 / 驳回整改 / 复议通过 / 驳回复议） */}
      <StatusActionModal
        visible={actionModal.visible}
        action={actionModal.action}
        recordSummary={
          actionModal.record
            ? `${actionModal.record.inspection} · ${actionModal.record.project_number}`
            : ''
        }
        submitting={submitting}
        onCancel={() =>
          setActionModal({ visible: false, action: null, record: null })
        }
        onConfirm={handleConfirmAction}
      />

      {/* 提交整改 */}
      <FixModal
        visible={!!fixModal}
        record={fixModal}
        dispatch={dispatch}
        submitting={submitting}
        onCancel={() => setFixModal(null)}
        onSubmit={handleFixSubmit}
      />

      {/* 发起复议 */}
      <ReviewModal
        visible={!!reviewModal}
        record={reviewModal}
        submitting={submitting}
        onCancel={() => setReviewModal(null)}
        onSubmit={handleReviewSubmit}
      />

      {/* 导出提示 */}
      <ExportModal
        visible={exportOpen}
        onClose={() => setExportOpen(false)}
        onGoToDownloads={onGoToDownloads}
      />
    </div>
  )
}

export default List
